/**
 * User Access Level — Lab `viewUser` / create-user for Strix.
 *
 * Staff console logins under the signed-in agent. Each staff row gets a
 * Full / View / Restrict matrix over Access Roles (Manage Downline, Book, …).
 *
 * Note: staff Keycloak sign-in is not wired yet — records + permissions are
 * stored now; login enforcement lands with identity binding.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  DEFAULT_PAGE_SIZE,
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  PageHead,
  Pager,
  SearchInput,
  StateBlock,
  type Column,
} from '@w7/ui'
import { count, EMPTY, lll } from '@/lib/format'
import { staffUsersLive, type StaffUser } from '@/api/map/staffUsers'
import { useStaffUsers } from '@/api/hooks/staff/useStaffUsers'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { canCreateStaffUser, canEditStaffUser } from '@/app/staffAccess'
import './users.css'

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function UsersPage() {
  const navigate = useNavigate()
  const { data: staffMe } = useStaffMe()
  const canCreate = canCreateStaffUser(staffMe)
  const canEdit = canEditStaffUser(staffMe)
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useStaffUsers({
    page,
    keyword: keyword.trim() || undefined,
  })

  const columns = useMemo<Column<StaffUser>[]>(
    () => [
      {
        id: 'username',
        header: 'User name',
        cell: (row) =>
          canEdit ? (
            <Link to={`/users/${row.id}`} className="w7-users-link">
              {row.username}
            </Link>
          ) : (
            <span>{row.username}</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <Chip tone={row.status === 'active' ? 'profit' : 'muted'}>
            {row.status === 'active' ? 'Active' : 'Inactive'}
          </Chip>
        ),
      },
      {
        id: 'access',
        header: 'Grants',
        cell: (row) => {
          const n = row.grants?.length ?? 0
          if (!n) return EMPTY
          return `${n} action${n === 1 ? '' : 's'}`
        },
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: (row) => lll(row.updatedAt),
      },
      {
        id: 'edit',
        header: '',
        cell: (row) =>
          canEdit ? (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/users/${row.id}`)}>
              Edit
            </Button>
          ) : null,
      },
    ],
    [navigate, canEdit],
  )

  const results = data?.results ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / (data?.pageSize ?? PAGE_SIZE)))
  const narrowed = Boolean(keyword.trim())

  return (
    <div className="w7-users-page">
      <PageHead
        title="User Access Level"
        subtitle="Staff logins under you — Menu → Access Role → actions. CM-only menus and Premium BM Management (not on the agent rail yet) are omitted."
        actions={
          canCreate ? (
            <Button variant="primary" onClick={() => navigate('/users/new')}>
              Create user
            </Button>
          ) : undefined
        }
      />

      {!staffUsersLive() ? (
        <Banner tone="info">
          Demo staff users — run hierarchy-service migration 013 for live CRUD.
        </Banner>
      ) : (
        <Banner tone="info">
          Staff accounts are stored now. Keycloak sign-in for staff users is not enabled yet —
          permissions will apply when identity binding lands.
        </Banner>
      )}

      <Card flush className="w7-users-panel">
        <div className="w7-users-toolbar">
          <SearchInput
            label="Search staff users"
            value={keyword}
            onChange={(value) => {
              setKeyword(value)
              setPage(1)
            }}
            placeholder="Search username"
            width={240}
          />
          <span className="w7-users-count" aria-live="polite">
            {isLoading && total === 0 ? '…' : `${count(total)} users`}
          </span>
        </div>

        {isError ? (
          <div className="w7-users-empty">
            <StateBlock icon="users" title="Could not load users" tone="error">
              Check hierarchy-service and try again.
            </StateBlock>
          </div>
        ) : (
          <>
            <DataTable
              label="Staff users"
              columns={columns}
              rows={results}
              rowKey={(r) => r.id}
              loading={isLoading}
              stickyOffset={0}
              empty={
                <StateBlock
                  icon="users"
                  title={narrowed ? 'No users match this search' : 'No staff users yet'}
                  actions={
                    narrowed ? (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setKeyword('')
                          setPage(1)
                        }}
                      >
                        Clear search
                      </Button>
                    ) : canCreate ? (
                      <Button variant="primary" onClick={() => navigate('/users/new')}>
                        Create user
                      </Button>
                    ) : undefined
                  }
                >
                  {narrowed
                    ? 'Try another username, or clear the search to see everyone.'
                    : 'Create a user to give someone limited console access under your account.'}
                </StateBlock>
              }
            />
            {total > 0 || isLoading ? (
              <div className="w7-users-foot">
                <Pager
                  page={page}
                  pageCount={pageCount}
                  onChange={setPage}
                  total={total}
                  pageSize={data?.pageSize ?? PAGE_SIZE}
                />
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  )
}
